import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, UtensilsCrossed, Clock, MapPin, Phone, Mail, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Restaurant = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const menuItems = [
    {
      category: "Vorspeisen",
      items: [
        { name: "Bruschetta", description: "Geröstetes Brot mit Tomaten und Basilikum", price: "6,50€" },
        { name: "Carpaccio", description: "Dünn geschnittenes Rindfleisch mit Parmesan", price: "9,90€" }
      ]
    },
    {
      category: "Hauptgerichte",
      items: [
        { name: "Rindersteak", description: "200g Rindersteak mit Pfeffersoße und Beilagen", price: "19,90€" },
        { name: "Lachsfilet", description: "Gegrillter Lachs mit Gemüse und Reis", price: "17,50€" },
        { name: "Pasta Carbonara", description: "Hausgemachte Pasta mit cremiger Sauce", price: "12,90€" }
      ]
    },
    {
      category: "Desserts",
      items: [
        { name: "Tiramisu", description: "Klassisches italienisches Dessert", price: "5,90€" },
        { name: "Panna Cotta", description: "Mit Beerenkompott", price: "5,50€" }
      ]
    }
  ];

  const features = [
    "Frische regionale Zutaten",
    "Täglich wechselnde Spezialitäten",
    "Gemütliche Atmosphäre",
    "Familienfreundlich",
    "Vegetarische Optionen",
    "Außenterrasse im Sommer"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/#portfolio" className="flex items-center gap-2 text-red-600 hover:text-red-700 transition-colors">
              <ArrowLeft size={20} />
              <span className="font-medium">Zurück zum Portfolio</span>
            </Link>
            <Button className="bg-red-500 hover:bg-red-600 text-white rounded-xl">
              Tisch reservieren
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 rounded-full text-red-700 text-sm font-medium">
                <UtensilsCrossed size={16} />
                <span>Kulinarische Erlebnisse</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-800 leading-tight">
                Genuss mit Tradition
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed">
                Willkommen in unserem Restaurant! Wir servieren Ihnen täglich frische, regionale Küche in gemütlicher Atmosphäre. Lassen Sie sich von unseren kulinarischen Kreationen verwöhnen.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-red-500 hover:bg-red-600 text-white rounded-xl shadow-lg">
                  Tisch reservieren
                </Button>
                <Button size="lg" variant="outline" className="border-2 border-red-300 text-slate-700 hover:bg-red-50 rounded-xl">
                  Speisekarte ansehen
                </Button>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1600891964599-f61ba0e24092?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxyZXN0YXVyYW50JTIwZm9vZHxlbnwwfHx8fDE3NzM1Nzk2ODh8MA&ixlib=rb-4.1.0&q=85"
                alt="Restaurant Food"
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Menu Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
              Unsere Speisekarte
            </h2>
            <div className="w-20 h-1 bg-red-500 mx-auto mb-6 rounded-full"></div>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Eine Auswahl unserer beliebtesten Gerichte
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {menuItems.map((section, index) => (
              <Card key={index} className="border-red-100 rounded-2xl">
                <CardContent className="pt-6">
                  <h3 className="text-2xl font-bold text-slate-800 mb-6 text-center">{section.category}</h3>
                  <div className="space-y-4">
                    {section.items.map((item, idx) => (
                      <div key={idx} className="border-b border-red-100 last:border-0 pb-4 last:pb-0">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-semibold text-slate-800">{item.name}</h4>
                          <span className="text-red-600 font-bold">{item.price}</span>
                        </div>
                        <p className="text-sm text-slate-600">{item.description}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-red-50 to-orange-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
              Impressionen
            </h2>
            <div className="w-20 h-1 bg-red-500 mx-auto rounded-full"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <img
              src="https://images.unsplash.com/photo-1550367363-ea12860cc124?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxyZXN0YXVyYW50JTIwZm9vZHxlbnwwfHx8fDE3NzM1Nzk2ODh8MA&ixlib=rb-4.1.0&q=85"
              alt="Gericht 1"
              className="rounded-2xl shadow-lg w-full h-64 object-cover hover:scale-105 transition-transform"
            />
            <img
              src="https://images.unsplash.com/photo-1502998070258-dc1338445ac2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwzfHxyZXN0YXVyYW50JTIwZm9vZHxlbnwwfHx8fDE3NzM1Nzk2ODh8MA&ixlib=rb-4.1.0&q=85"
              alt="Gericht 2"
              className="rounded-2xl shadow-lg w-full h-64 object-cover hover:scale-105 transition-transform"
            />
            <img
              src="https://images.unsplash.com/photo-1651440204227-a9a5b9d19712?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHw0fHxyZXN0YXVyYW50JTIwZm9vZHxlbnwwfHx8fDE3NzM1Nzk2ODh8MA&ixlib=rb-4.1.0&q=85"
              alt="Restaurant Atmosphäre"
              className="rounded-2xl shadow-lg w-full h-64 object-cover hover:scale-105 transition-transform"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-6">
                Was uns auszeichnet
              </h2>
              <div className="grid grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Star className="text-white" size={14} />
                    </div>
                    <span className="text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <Card className="border-red-100 rounded-2xl shadow-xl">
              <CardContent className="pt-6 space-y-6">
                <h3 className="text-2xl font-bold text-slate-800">Öffnungszeiten</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Clock className="text-red-600" size={20} />
                    <div>
                      <div className="font-semibold text-slate-800">Di - Fr</div>
                      <div className="text-slate-600">11:30 - 14:30 & 17:30 - 22:00</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="text-red-600" size={20} />
                    <div>
                      <div className="font-semibold text-slate-800">Sa - So</div>
                      <div className="text-slate-600">12:00 - 23:00</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="text-red-600" size={20} />
                    <div>
                      <div className="font-semibold text-slate-800">Montag</div>
                      <div className="text-slate-600">Ruhetag</div>
                    </div>
                  </div>
                </div>
                <Button className="w-full bg-red-500 hover:bg-red-600 text-white rounded-xl">
                  Jetzt reservieren
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-red-50 to-orange-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-6">
            Besuchen Sie uns
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Wir freuen uns auf Ihren Besuch - reservieren Sie noch heute Ihren Tisch
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card className="border-red-100 rounded-2xl">
              <CardContent className="pt-6 text-center">
                <Phone className="text-red-600 mx-auto mb-3" size={32} />
                <div className="font-semibold text-slate-800 mb-1">Reservierung</div>
                <div className="text-slate-600">+49 (0) 6107 456789</div>
              </CardContent>
            </Card>
            <Card className="border-red-100 rounded-2xl">
              <CardContent className="pt-6 text-center">
                <Mail className="text-red-600 mx-auto mb-3" size={32} />
                <div className="font-semibold text-slate-800 mb-1">E-Mail</div>
                <div className="text-slate-600">info@restaurant-genuss.de</div>
              </CardContent>
            </Card>
            <Card className="border-red-100 rounded-2xl">
              <CardContent className="pt-6 text-center">
                <MapPin className="text-red-600 mx-auto mb-3" size={32} />
                <div className="font-semibold text-slate-800 mb-1">Adresse</div>
                <div className="text-slate-600">Marktplatz 8, Kelsterbach</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-800 text-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-slate-300">
            © 2025 Restaurant Genuss • Erstellt von{' '}
            <Link to="/#portfolio" className="text-red-400 hover:text-red-300">
              WebWerk Kelsterbach
            </Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Restaurant;